import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  fetchScopeControlCounts,
  fetchScopeEntities,
  fetchScopeMateriality,
  fetchScopeOverdueSummary,
  fetchScopePlannedCompleted,
  fetchScopeRiskSummary,
  fetchScopeSystems,
  fetchScopeTestingStatusRollup,
  type ScopeControlCountsResponse,
  type ScopeEntitiesResponse,
  type ScopeMaterialityResponse,
  type ScopeOverdueSummaryResponse,
  type ScopePlannedCompletedResponse,
  type ScopeRiskSummaryResponse,
  type ScopeSystemsResponse,
  type ScopeTestingStatusRollupResponse,
} from '../../request/analytics/scopeReports.ts';
import { analyticsScopeQueryKeys } from '../../queries/analytics/keys.ts';
import { useAnalyticsScope } from './useAnalyticsScope.tsx';
import { scopeKey } from '../../request/analytics/scope.ts';

/**
 * React Query v5 hooks for the cross-persona `/api/analytics/scope/*`
 * endpoints (feedback round 2026-08) — shared "scope & coverage" data reused
 * across the CFO, CAE, Controller, and Auditor dashboards (and the
 * self-serve gallery). Each report is its own independent query, same
 * pattern as every other persona's hooks file.
 */

export function useScopeControlCounts(): UseQueryResult<ScopeControlCountsResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.controlCounts(scopeKey(scope)), queryFn: () => fetchScopeControlCounts(scope) });
}

export function useScopeRiskSummary(): UseQueryResult<ScopeRiskSummaryResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.riskSummary(scopeKey(scope)), queryFn: () => fetchScopeRiskSummary(scope) });
}

export function useScopeSystems(): UseQueryResult<ScopeSystemsResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.systems(scopeKey(scope)), queryFn: () => fetchScopeSystems(scope) });
}

export function useScopeEntities(): UseQueryResult<ScopeEntitiesResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.entities(scopeKey(scope)), queryFn: () => fetchScopeEntities(scope) });
}

export function useScopeMateriality(): UseQueryResult<ScopeMaterialityResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.materiality(scopeKey(scope)), queryFn: () => fetchScopeMateriality(scope) });
}

export function useScopePlannedCompleted(): UseQueryResult<ScopePlannedCompletedResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.plannedCompleted(scopeKey(scope)), queryFn: () => fetchScopePlannedCompleted(scope) });
}

export function useScopeOverdueSummary(): UseQueryResult<ScopeOverdueSummaryResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({ queryKey: analyticsScopeQueryKeys.overdueSummary(scopeKey(scope)), queryFn: () => fetchScopeOverdueSummary(scope) });
}

export function useScopeTestingStatusRollup(): UseQueryResult<ScopeTestingStatusRollupResponse, Error> {
  const scope = useAnalyticsScope();
  return useQuery({
    queryKey: analyticsScopeQueryKeys.testingStatusRollup(scopeKey(scope)),
    queryFn: () => fetchScopeTestingStatusRollup(scope),
  });
}
